
import { NotificationCenter } from "@/components/NotificationCenter";
import { UserMenu } from "@/components/UserMenu";
import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";
import { SetupProgressTracker } from "@/components/SetupProgressTracker";
import { useSetupProgress } from "@/hooks/useSetupProgress";
import { useIsAdmin } from '@/hooks/useAnalytics';
import { Button } from "@/components/ui/button";
import { ShieldCheck } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { SmplifyLogo } from "@/components/SmplifyLogo";

interface HeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
}

export function Header({
  className,
  children,
  ...props
}: HeaderProps) {
  const {
    setupSteps,
    isLoading,
    isLoggedIn,
    dismissed,
    dismiss,
  } = useSetupProgress();
  
  const {
    data: isAdmin
  } = useIsAdmin();
  
  // Show the tracker while there's at least one incomplete step OR while
  // everything is complete but the user hasn't dismissed the celebratory
  // state yet. After dismissal, hide it permanently for this browser.
  const showProgressTracker = isLoggedIn &&
    !isLoading &&
    !dismissed &&
    setupSteps.length > 0;

  return <header
      className={cn(
        "flex min-h-14 md:min-h-16 md:h-16 items-center gap-2 md:gap-4 border-b border-border bg-background/95 backdrop-blur-md px-3 md:px-4 overflow-hidden",
        className
      )}
      style={{
        paddingTop: "env(safe-area-inset-top)",
        // Ensure the inner content area keeps its full height beneath the status-bar inset
        boxSizing: "content-box",
      }}
      {...props}>
      {children}
      <div className="flex items-center md:ml-[-1rem]">
        <Link to="/getting-started" aria-label="Smplify home">
          <SmplifyLogo
            size="header"
            className="cursor-pointer hover:opacity-90 transition-opacity"
          />
        </Link>
      </div>
      <div className="hidden sm:flex items-center ml-[-1rem]">
        <Separator orientation="vertical" className="h-4 mx-1" />
        <span className="text-sm font-medium text-brand-main">Partner Portal</span>
      </div>
      <div className="flex-1" />
      <div className="flex items-center gap-1 md:gap-2">
        {showProgressTracker && (
          <div className="hidden lg:block">
            <SetupProgressTracker steps={setupSteps} onDismiss={dismiss} />
          </div>
        )}
        <NotificationCenter />
        <UserMenu />
      </div>
    </header>;
}

